/* Clients for the v2.1 data features: dataset rows grid, design score,
 * dashboard suggestions, report templates. */
import type { DraftCheck } from "./admin";
import { apiRequest } from "./client";

export type Area = "admin" | "owner";

// ---- dataset rows -----------------------------------------------------------

export type ColumnKind = "number" | "date" | "text" | "boolean";

export interface RowsColumn {
  name: string;
  display_name: string;
  data_type: string;
  role: string;
  kind: ColumnKind;
}

export interface RowsPage {
  columns: RowsColumn[];
  rows: unknown[][];
  total: number;
  total_rows: number;
  offset: number;
  limit: number;
  status: "live" | "cached" | "delayed" | "unavailable";
  error: string | null;
  last_refreshed: string | null;
  source_updated_at: string | null;
  truncated: boolean;
  max_rows: number;
  reviewSource?: string;
}

export interface RowsRequest {
  offset: number;
  limit: number;
  search?: string;
  sort?: { field: string; direction: "asc" | "desc" } | null;
  refresh?: boolean;
}

export const rowsApi = {
  page: (area: Area, datasetId: string, body: RowsRequest) =>
    apiRequest<RowsPage>(`/api/v1/${area}/datasets/${datasetId}/rows`, { method: "POST", body }),
};

// ---- design score -------------------------------------------------------------

export interface DesignIssue {
  severity: "high" | "medium" | "low";
  message: string;
  widgetId: string | null;
  pageId: string | null;
  fix: string;
  category?: string;
  code?: string;
}

export interface DesignScore {
  score: number;
  grade: string;
  summary: string;
  categories: Array<{ key: string; label: string; score: number }>;
  issues: DesignIssue[];
  issueCount?: number;
  pages?: Array<{ pageId: string; name: string; score: number }>;
}

export type DraftCheckWithDesign = DraftCheck & { design?: DesignScore };

export const designApi = {
  /** /validate with an optional unsaved definition (the designer's live badge). */
  validate: (area: Area, reportId: string, definition?: unknown) =>
    apiRequest<DraftCheckWithDesign>(`/api/v1/${area}/reports/${reportId}/validate`, {
      method: "POST",
      body: definition === undefined ? undefined : { definition },
    }),
};

// ---- suggestions ----------------------------------------------------------------

export interface MeasureToCreate {
  name: string;
  display_name: string;
  source_field: string;
  aggregation: string;
}

export interface ProfileField {
  name: string;
  label: string;
  kind: string;
  kindLabel: string;
  cardinality: number | null;
  nullRatio: number | null;
  examples: string[];
  note: string | null;
}

export interface DatasetProfile {
  datasetId: string;
  datasetName: string;
  rowCount: number;
  sampleSize: number;
  fields: ProfileField[];
  metrics: Array<{ name: string; label: string; existing: boolean; aggregation: string | null; source_field: string | null }>;
  notes: string[];
}

export interface Proposal {
  id: string;
  name: string;
  description: string;
  why: string[];
  definition: Record<string, any>;
  measuresToCreate: MeasureToCreate[];
  designScore: { score: number; grade: string; summary: string };
  visuals: Array<{ type: string; title: string }>;
}

export interface SuggestSource {
  status: string;
  error: string | null;
  rowCount: number;
  truncated: boolean;
}

export interface SuggestedVisual {
  title: string;
  why: string;
  group: string;
  widget: Record<string, any>;
  measuresToCreate: MeasureToCreate[];
}

export const suggestApi = {
  proposals: (datasetId: string, body?: { reportId?: string; reportName?: string }) =>
    apiRequest<{ profile: DatasetProfile; proposals: Proposal[]; source: SuggestSource }>(`/api/v1/owner/datasets/${datasetId}/suggest`, { method: "POST", body: body ?? {} }),
  visuals: (datasetId: string) =>
    apiRequest<{ visuals: SuggestedVisual[]; profile: DatasetProfile; source: SuggestSource }>(`/api/v1/owner/datasets/${datasetId}/suggest-visuals`, { method: "POST" }),
  ensureMeasures: (datasetId: string, measures: MeasureToCreate[]) =>
    apiRequest<{ created: string[]; existing: string[] }>(`/api/v1/owner/datasets/${datasetId}/measures/ensure`, { method: "POST", body: { measures } }),
};

// ---- templates -----------------------------------------------------------------

export interface LayoutBox {
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutSummary {
  pageCount: number;
  widgetCount: number;
  datasetCount: number;
  visualTypes: Record<string, number>;
  theme?: string | null;
  pages: Array<{ name: string; columns: number; rows: number; filters: number; widgets: LayoutBox[] }>;
}

export interface ReportTemplateItem {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  keeps_bindings: boolean;
  is_builtin: boolean;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  summary: LayoutSummary;
  definition: Record<string, any> | null;
}

export const templatesApi = {
  list: () => apiRequest<ReportTemplateItem[]>("/api/v1/owner/templates"),
  get: (id: string) => apiRequest<ReportTemplateItem>(`/api/v1/owner/templates/${id}`),
  create: (body: { name: string; description?: string; category?: string; definition: Record<string, unknown>; keepBindings: boolean; sourceReportId?: string }) =>
    apiRequest<ReportTemplateItem>("/api/v1/owner/templates", { method: "POST", body }),
  update: (id: string, body: { name?: string; description?: string | null; category?: string | null }) =>
    apiRequest<ReportTemplateItem>(`/api/v1/owner/templates/${id}`, { method: "PATCH", body }),
  remove: (id: string) => apiRequest<void>(`/api/v1/owner/templates/${id}`, { method: "DELETE" }),
};

/** Same summary shape the templates API returns, for any definition (built-in
 * templates, suggestions, a report's draft). */
export function summarizeDefinition(def: any): LayoutSummary {
  const BAR = new Set(["filter", "date_filter", "text_filter"]);
  const pages = [...(def?.pages ?? [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
  const visualTypes: Record<string, number> = {};
  const datasets = new Set<string>();
  let widgetCount = 0;
  for (const pg of pages) for (const w of pg.widgets ?? []) { widgetCount++; visualTypes[w.type] = (visualTypes[w.type] ?? 0) + 1; if (w.datasetId) datasets.add(w.datasetId); }
  return {
    pageCount: pages.length,
    widgetCount,
    datasetCount: datasets.size,
    visualTypes,
    theme: def?.theme ?? null,
    pages: pages.slice(0, 6).map((pg: any, i: number) => {
      const c = pg.canvas ?? {};
      return {
        name: pg.name ?? `Page ${i + 1}`,
        columns: c.columns ?? 24,
        rows: Math.max(1, Math.floor(((c.height ?? 944) + (c.gap ?? 8)) / ((c.rowHeight ?? 20) + (c.gap ?? 8)))),
        filters: (pg.widgets ?? []).filter((w: any) => BAR.has(w.type)).length,
        widgets: (pg.widgets ?? []).filter((w: any) => w.layout && !BAR.has(w.type)).map((w: any) => ({ type: w.type, x: w.layout.x, y: w.layout.y, w: w.layout.w, h: w.layout.h })),
      };
    }),
  };
}
